/**
 * The Forecast tab's filter panel.
 *
 * CR-6448: one shell for both views, matching the Deliveries tab — the header
 * (title, Refresh, Export, Filters toggle with active count) stays visible
 * when the panel is collapsed; only the sections and the
 * Clear all / Apply filters footer hide. The supplier view renders the design's
 * four sections (`ForecastFilterSupplierLayout`); the customer view keeps its
 * CR-6440 body (`ForecastFilterSections`). Only the section body differs.
 */
import { ForecastFilterOptions } from '../../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { Download, Filter, Loader2, RefreshCw, Search, X } from 'lucide-react';
import { ForecastFilterSections } from './ForecastFilterSections';
import { ForecastFilterSupplierLayout } from './ForecastFilterSupplierLayout';
import { ForecastCascadeFilterOptions } from './useForecastFilterCascades';
import { ForecastFilters } from './forecastGridTypes';

interface ForecastFiltersPanelProps {
  filters: ForecastFilters;
  onFilterChange: (key: string, value: string) => void;
  activeFilterCount: number;
  onApply: () => void;
  onClear: () => void;
  /** Collapsed state. The header stays visible when collapsed. */
  expanded: boolean;
  onShowFilters: () => void;
  onHideFilters: () => void;
  /**
   * CR-6440: always rendered, as on the Dashboard filter bar. The caller
   * decides what a refresh means — a snapshot rebuild on snapshot installs, a
   * plain grid re-fetch everywhere else.
   */
  onRefresh: () => void;
  /** Disables Refresh and spins its icon while the reload is in flight. */
  refreshing?: boolean;
  /** Tooltip for Refresh, e.g. "Last refreshed 14/09/2026, 10:00". */
  refreshTitle?: string;
  exporting: boolean;
  onExport: (format: 'csv' | 'xlsx') => void;
  filterOptions: ForecastFilterOptions | null;
  cascadeFilterOptions: ForecastCascadeFilterOptions | null;
  isCustomer: boolean;
  isSupplier: boolean;
  isScheduleTenant: boolean;
  supplierSiteName?: string;
}

export function ForecastFiltersPanel({
  filters, onFilterChange, activeFilterCount, onApply, onClear,
  expanded, onShowFilters, onHideFilters, onRefresh, refreshing = false, refreshTitle,
  exporting, onExport, filterOptions, cascadeFilterOptions, isCustomer, isSupplier,
  isScheduleTenant, supplierSiteName,
}: ForecastFiltersPanelProps) {
  const filterRecord = filters as unknown as Record<string, string>;

  const body = isSupplier ? (
    <ForecastFilterSupplierLayout
      filters={filterRecord}
      onFilterChange={onFilterChange}
      siteOptions={(filterOptions?.customers || []).map((c) => ({ value: String(c.id), label: c.name }))}
      warehouseOptions={(filterOptions?.warehouses || []).map((w) => ({ value: w.code, label: w.name }))}
      supplierGroupOptions={(filterOptions?.supplier_groups || []).map((g) => ({ value: g.name, label: g.name }))}
      supplierSiteName={supplierSiteName}
      onApply={onApply}
    />
  ) : (
    // CR-6440 rework: the customer body draws its own shaded sections and
    // inset dividers (PO Lines layout), so it takes the card's full width.
    <ForecastFilterSections
      filters={filterRecord}
      onFilterChange={onFilterChange}
      filterOptions={filterOptions}
      isCustomer={isCustomer}
      isScheduleTenant={isScheduleTenant}
      divisionOptions={cascadeFilterOptions?.divisions.map((d) => ({ value: String(d.id), label: d.name })) || []}
      siteCountries={cascadeFilterOptions?.siteCountries || []}
      supplierCountries={cascadeFilterOptions?.supplierCountries || []}
      siteOptions={cascadeFilterOptions?.sites.map((s) => ({ value: String(s.id), label: s.name })) || []}
      rawCascadeOptions={cascadeFilterOptions}
    />
  );

  // CR-6440 rework: the customer footer matches PO Lines (full-size buttons,
  // "Clear All" / "Apply Filters"); the supplier footer keeps the Deliveries
  // shell it was signed off against (CR-6448).
  const footer = isSupplier ? (
    <div className="flex items-center justify-between px-6 pb-4">
      <Button size="sm" variant="outline" onClick={onClear}>
        <X className="h-3.5 w-3.5 mr-1.5" />
        Clear all
      </Button>
      <Button size="sm" onClick={onApply}>
        <Search className="h-3.5 w-3.5 mr-1.5" />
        Apply filters
      </Button>
    </div>
  ) : (
    <div className="flex items-center justify-between px-6 py-4">
      <Button type="button" variant="outline" onClick={onClear} className="px-6">
        <X className="h-4 w-4 mr-2" />
        Clear All
      </Button>
      <Button type="button" onClick={onApply} className="px-6">
        <Search className="h-4 w-4 mr-2" />
        Apply Filters
      </Button>
    </div>
  );

  return (
    <Card>
      {/* Filter header — stays visible when the panel is collapsed. Button
          sizes and icons match the PO Lines panel header. */}
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-xl">Filter Forecast Schedule</CardTitle>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={onRefresh} disabled={refreshing} title={refreshTitle}>
              {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Refresh
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" size="sm" variant="outline" disabled={exporting}>
                  {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onExport('csv')}>Export as CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport('xlsx')}>Export as Excel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={expanded ? onHideFilters : onShowFilters}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                expanded
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  expanded
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className={isSupplier ? 'space-y-4 p-0' : 'p-0'}>
          {body}
          {/* Filter footer — Clear left, Apply right. */}
          {footer}
        </CardContent>
      )}
    </Card>
  );
}
